import { useState, useEffect } from 'react';
import { useAuth } from '../modules/Shared/contexts/AuthContext';
import api from '../modules/Shared/services/api';
import type { SubscriptionInfo, BillingActions } from '../modules/Shared/types';

export interface Tier {
  id: number;
  name: string;
  display_name: string;
  price_monthly: number;
  student_limit: number;
  features: string[];
}

export interface UserTierData {
  current_tier: Tier | null;
  is_inherited: boolean;
  remaining_student_slots: number;
  features: string[];
  subscription: SubscriptionInfo | null;
  billing_actions: BillingActions | null;
}

interface UseTierReturn {
  currentTier: Tier | null;
  features: string[];
  remainingSlots: number;
  canUpgrade: boolean;
  isInherited: boolean;
  subscription: SubscriptionInfo | null;
  billingActions: BillingActions | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useTier = (): UseTierReturn => {
  const { user } = useAuth();
  const [tierData, setTierData] = useState<UserTierData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTierData = async (): Promise<void> => {
    if (!user) {
      setTierData(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await api.getTierData();
      setTierData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tier data');
      console.error('Error fetching tier data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchTierData();
    // fetchTierData is stable and doesn't need to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return {
    currentTier: tierData?.current_tier || null,
    features: tierData?.features || [],
    remainingSlots: tierData?.remaining_student_slots || 0,
    canUpgrade: tierData?.billing_actions?.can_upgrade ?? false,
    isInherited: tierData?.is_inherited || false,
    subscription: tierData?.subscription ?? null,
    billingActions: tierData?.billing_actions ?? null,
    isLoading,
    error,
    refetch: fetchTierData,
  };
};
